import { useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RouteComponentProps } from "react-router";
import { useNavigate } from "react-router-dom-v5-compat";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import {
  DisruptionExerciseProgress,
  DisruptionUniversalExercise,
  getDisruptionExerciseProgress,
} from "@b2bportal/core-disruption";
import { getBookingSupportId, trackEvent } from "@hopper-b2b/api";
import { PATH_TRIPS } from "@hopper-b2b/common-utils";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  BookingType,
  CfarExerciseProgress,
  ChfarExerciseProgress,
  ClientName,
  FlightItineraryState,
  HotelItineraryState,
  type IApiConfig,
  MyTripsFilter,
  MyTripsModalTypes,
  VIEWED_MY_TRIPS,
} from "@hopper-b2b/types";
import { B2BSpinner, LoadingPopup, TripsHeader } from "@hopper-b2b/ui";
import {
  getEnvVariables,
  updateApiConfigAction,
  useDeviceTypes,
  useEnablePriceFreeze,
} from "@hopper-b2b/utilities";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import { ClientContext } from "../../App";
import { tripsApiConfigStoreKey } from "../../reducers";
import { addFlightType } from "../TripsList/components/ItineraryList/components/FlightCard/helpers";
import {
  getAirportMap,
  getCfarExerciseProgress,
  getChfarExerciseProgress,
} from "../TripsList/reducer";
import { WatchDetailsModalContent } from "../UberTripsList/components/WatchDetailsModalContent";
import { FilterTabs } from "./components/FilterTabs";
import { FlightItineraryDetails } from "./components/FlightItineraryDetails";
import { HotelItineraryDetails } from "./components/HotelItineraryDetails";
import { ItineraryList } from "./components/ItineraryList";
import { ItinerariesModal } from "./components/ItineraryList/components/ItinerariesModal";
import { NoTripResults } from "./components/NoResults";
import { TripSearch } from "./components/TripSearch";
import { WatchList } from "./components/WatchList";
import type { TripsListConnectorProps } from "./container";
import "./styles.scss";

export interface ITripsListProps
  extends TripsListConnectorProps,
    RouteComponentProps {
  isMobile?: boolean;
  apiConfig: IApiConfig;
  onSupportClick: () => void;
}

export const ApacTripsList = ({
  // fetchCars,
  fetchFlights,
  fetchHotels,
  history,
  isMobile,
  listWatches,
  selectedFlight,
  selectedHotel,
  selectedCar,
  selectedWatch,
  tripsFilter,
  tripsLoading,
  hasError,
  hasTripsToDisplay,
  openModal,
  listPriceFreeze,
  apiConfig,
  tripSearchResults,
  setOpenModal,
}: ITripsListProps) => {
  const { t } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();
  const dispatch = useDispatch();
  const clientContext = useContext(ClientContext);
  const navigate = useNavigate();

  const isUserAuthenticated = useMemo(
    () => !!clientContext?.sessionInfo?.userInfo,
    [clientContext?.sessionInfo]
  );

  const showFlightPriceFreeze = useEnablePriceFreeze();
  const cfarExerciseProgress = useSelector(getCfarExerciseProgress);
  const chfarExerciseProgress = useSelector(getChfarExerciseProgress);
  const disruptionExerciseProgress = useSelector(getDisruptionExerciseProgress);
  const airports = useSelector(getAirportMap);

  const onOpenModal = useCallback(
    (type: MyTripsModalTypes, flight: BookedFlightItineraryWithDepartureTime) =>
      setOpenModal({
        type,
        selectedItinerary: addFlightType(flight),
      }),
    [setOpenModal]
  );

  useEffect(() => {
    dispatch(updateApiConfigAction(tripsApiConfigStoreKey, apiConfig));
  }, [apiConfig, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent({
      eventName: VIEWED_MY_TRIPS,
      properties: {},
    });
  }, []);

  const fetchAllItineraries = useCallback(() => {
    // fetchCars({}, history);
    fetchFlights(
      {
        states: [
          FlightItineraryState.Canceled,
          FlightItineraryState.Future,
          FlightItineraryState.Present,
          FlightItineraryState.Past,
        ],
        referenceDateTime: dayjs().format(),
      },
      history
    );
    fetchHotels(
      {
        states: [
          HotelItineraryState.Canceled,
          HotelItineraryState.Past,
          HotelItineraryState.Future,
          HotelItineraryState.Present,
        ],
        referenceDateTime: dayjs().format(),
      },
      history
    );
  }, [fetchFlights, fetchHotels, history]);

  useEffect(() => {
    if (isUserAuthenticated) {
      fetchAllItineraries();
      listWatches(history); // TODO recheck if it is possible to fetch watches for non-authenticated users
    }
    if (showFlightPriceFreeze) listPriceFreeze();
  }, [
    isUserAuthenticated,
    fetchAllItineraries,
    listPriceFreeze,
    showFlightPriceFreeze,
    history,
    listWatches,
  ]);

  const renderFilteredList = () => {
    switch (tripsFilter) {
      case MyTripsFilter.UPCOMING_TRIPS:
      case MyTripsFilter.PAST_TRIPS:
      case MyTripsFilter.FLIGHTS:
      case MyTripsFilter.HOTELS:
        return <ItineraryList isMobile={isMobile} />;
      case MyTripsFilter.WATCHED_TRIPS:
        return <WatchList isMobile={isMobile} />;
      case MyTripsFilter.PRIZE_FREEZES:
        return <Box />;
    }
  };

  const contactSupportAction = useMemo(
    () => async () => {
      // todo(isabela): remove when moved to core-trips
      const isHSBC =
        getEnvVariables("clientName") === ClientName.HSBC ||
        getEnvVariables("clientName") === ClientName.HSBC_IN;
      if (isHSBC) {
        const supportId = await getBookingSupportId(
          selectedFlight.bookedItinerary.id,
          BookingType.Flight
        );
        navigate({
          pathname: "/trips/customerSupport",
          search: `supportId=${supportId}`,
        });
      } else {
        onOpenModal(MyTripsModalTypes.ContactSupport, selectedFlight);
      }
    },
    [navigate, onOpenModal, selectedFlight]
  );

  const selectedItinerary = useMemo(() => {
    const renderCfarExercise = !(
      cfarExerciseProgress === CfarExerciseProgress.NotStarted
    );

    const renderChfarExercise = !(
      chfarExerciseProgress === ChfarExerciseProgress.NotStarted
    );

    const renderDisruptionExercise =
      !(disruptionExerciseProgress === DisruptionExerciseProgress.NotStarted) &&
      !(disruptionExerciseProgress === undefined);

    return (
      <>
        {selectedFlight ? (
          renderDisruptionExercise ? (
            <DisruptionUniversalExercise
              disruptedFlight={selectedFlight}
              onContactSupport={contactSupportAction}
              handleExerciseCompleted={() => {
                // reload the trips and navigate to selected itinerray
                window.location.href = `/${PATH_TRIPS}/?tripId=${selectedFlight.bookedItinerary.id}`;
              }}
              displayExternalLinkAsModal={true}
              airports={airports}
            />
          ) : (
            <FlightItineraryDetails isMobile={isMobile} />
          )
        ) : null}
        {selectedWatch ? <WatchDetailsModalContent /> : null}
        {selectedHotel ? (
          <HotelItineraryDetails isMobile={matchesMobile} />
        ) : null}
        <ItinerariesModal isMobile={isMobile} />
      </>
    );
  }, [
    cfarExerciseProgress,
    chfarExerciseProgress,
    disruptionExerciseProgress,
    selectedFlight,
    selectedWatch,
    selectedHotel,
    matchesMobile,
    isMobile,
    onOpenModal,
  ]);

  return selectedFlight || selectedHotel || selectedCar || selectedWatch ? (
    selectedItinerary
  ) : (
    <Box className={clsx({ mobile: isMobile }, "trips-list", "apac")}>
      {isUserAuthenticated ? (
        <>
          <TripsHeader
            className="trips-header"
            tripHeaderCopy={{
              title: t("trips"),
              supportCTA: t("contactSupport"),
              subtitle: t("tripsSubtitle"),
            }}
            isMobile={matchesMobile}
            showSubtitleForMobile={true}
          />
          <FilterTabs mobile={isMobile} />
          <div
            id={`${tripsFilter}-tab`}
            role="tabpanel"
            aria-labelledby={tripsFilter}
          >
            {tripsLoading && (
              <LoadingPopup
                open={tripsLoading || tripsLoading === null}
                indicator={B2BSpinner}
                message={t("fetchingTrips")}
                classes={[
                  "trips-loading-modal",
                  ...(isMobile ? ["mobile"] : []),
                ]}
              />
            )}
            {!tripsLoading && <NoTripResults isMobile={isMobile} />}
            {!hasError &&
              (hasTripsToDisplay || openModal) &&
              renderFilteredList()}
          </div>
        </>
      ) : (
        <TripSearch isMobile={isMobile} tripSearchResults={tripSearchResults} />
      )}
    </Box>
  );
};
